import { SUPPORTED_CHAIN_IDS } from '@emberswap/emberswap-sdk2/dist/constants'
import { Menu } from '@headlessui/react'
import Link from 'next/link'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useConnect, useDisconnect } from 'wagmi'
import { InjectedConnector } from 'wagmi/connectors/injected'

import { classNames } from '../../../functions'
import { shortenAddress } from '../../../functions/address'
import useENSName from '../../../hooks/useENSName'
import { useDebounce } from '../../../hooks/utils'
import { useIsConnected, useSessionChainId } from '../../../state/application/hooks'
import useActiveWeb3React from '../../../wagmi/hooks/useActiveWeb3React'
import { AnimationTransition } from '../../Animation'
import CopyHelper from '../../Copy'
import { Injected, Logout, MetamaskIcon, Settings, WalletConnectIcon } from '../../Icons'
import { Avatar } from '../Avatar'
import NetworkSwitch from '../NetworkSwitch'
import { AccountSettings } from './AccountSettings'
import { ConnectDropdown } from './types'

const Icons: Record<string, ReactNode> = {
  Injected: <Injected width={16} height={16} />,
  MetaMask: <MetamaskIcon width={16} height={16} />,
  WalletConnect: <WalletConnectIcon width={16} height={16} />,
}

/**
 * @return {JSX.Element}
 */
export default function Account() {
  const { t } = useTranslation()
  const { isReconnecting, isConnected, account, connector, chain } = useActiveWeb3React()
  const [chainId] = useSessionChainId()
  const { ENSName } = useENSName(!chain?.unsupported && account ? account :  undefined)
  const { connectors, connect } = useConnect({ connector: new InjectedConnector() })

  const [debouncedHover, setDebouncedHover] = useState(false)
  const [hover, setHover] = useState(false)
  const [settings, setSettings] = useState(false)
  const { disconnect } = useDisconnect()

  const DebouncedHover = useDebounce([debouncedHover, hover], 200)
  useEffect(() => {
    const [debouncedHover] = DebouncedHover
    setHover(debouncedHover)
  }, [DebouncedHover])

  return (
    <>
      <MobileWrapper>
        <AnimatedButton isConnected={isConnected}>
          <NetworkSwitch supportedNetworks={SUPPORTED_CHAIN_IDS}>
            <ConnectDropdown   
              hover={hover}
              ENSName={ENSName}
              chainId={chainId}
              account={account}
                    isConnected={isConnected}
                    isReconnecting={isReconnecting}
                    connect={connect}
                    disconnect={disconnect}
                    connectors={connectors}
                    t={t}
                    setSettings={setSettings}
                    setDebouncedHover={setDebouncedHover}
                    setHover={setHover}
                  />

          </NetworkSwitch>
        </AnimatedButton>
        <AccountSettings
          settings={settings}
          setSettings={setSettings}
          show={settings}
          account={account}
          chainId={chainId}
          ENSName={ENSName}
          connector={connector}
          connectors={connectors}
          connect={connect}
          disconnect={disconnect}
          isConnected={isConnected}
          chain={chain}
        />
      </MobileWrapper>
    </>
  )
}

/**
 * @return {React.ElementType<any>} Dropdown content
 */
function ConnectDropdown({
  t,
  setDebouncedHover,
  setHover,
  setSettings,
  isConnected,
  isReconnecting,
  connectors,
  connect,
  disconnect,
  hover,
  account,
  ENSName,
  chainId
}: ConnectDropdown) {
  const [, { updateConnection }] = useIsConnected()
  const { isConnecting, pendingConnection } = useActiveWeb3React()

  return (
    <span className="flex items-center w-full group-one">
      <Menu.Button
        onMouseEnter={() => {
          setHover(true)
          setDebouncedHover(true)
        }}
        onMouseLeave={() => setDebouncedHover(false)}
        as="div"
        className="relative duration-500 ease-in-out flex justify-center  w-full items-center h-[35px] gap-2 px-3 rounded-lg cursor-pointer text-xxs font-light tracking-default bg-buttonbg hover:opacity-100 group-one-hover:bg-test-400 text-primary"
      >
        <>
          {account ? (
            <>
              <CopyHelper toCopy={ENSName ?? account}>
                {ENSName ??  shortenAddress(account)}
              </CopyHelper>
              <div className="-m-1 overflow-hidden border border-orange-300 rounded-lg">
                <div className="duration-500 hover:scale-125">
                  <Avatar width={25} height={25} chainId={chainId} ENSName={ENSName} />
                </div>
              </div>
            </>
          ) : isConnecting ? (
            t('Connecting')
          ) : pendingConnection ? (
            t('Authorize Wallet')
          ) : (
            t('Connect Wallet')
          )}
        </>
      </Menu.Button>

      <AnimationTransition show={hover}>
        <Menu.Items
          onMouseEnter={() => {
            setHover(true)
            setDebouncedHover(true)
          }}
          onMouseLeave={() => setDebouncedHover(false)}
          as="div"
          className="right-0 left-0 mx-auto group-one origin-top absolute bottom-full mt-0 mb-3 lg:bottom-auto landscape:bottom-auto lg:top-full landscape:top-full top-auto landscape:mb-0 lg:mb-0 landscape:mt-2 lg:mt-2 z-50  min-w-[240px] max-h-60 px-2 py-2 gap-x-4 w-fit overflow-hidden  rounded-lg shadow-lg ring-opacity-5 focus:outline-none"
        >
          <Menu.Item as="div" className="z-50 grid">
            {!isConnected && !isReconnecting ? (
              <>
                {connectors.map((connector) => (
                  <Menu.Button
                    key={connector.id}
                    onClick={() => {
                      connect({ connector })
                      updateConnection(true)
                    }}
                    as="div"
                    className="z-50 hover:opacity-80 rounded-lg cursor-pointer transform-all h-[40px] text-sm hover:bg-gray-500  flex items-center w-full gap-2 px-2 text-primary "
                  >
                    <div className="-ml-[6px] w-auto rounded-full px-1 pl-2">
                      {Icons[connector.name] && Icons[connector.name]}
                    </div>
                    <span className="justify-center px-4 font-thin text-xxs tracking-default">
                      {connector.name}
                    </span>
                  </Menu.Button>
                ))}
                <Menu.Button className="z-50 grid justify-center px-4 -mt-0.5 font-thin text-4xs tracking-default  rounded-lg cursor-pointer h-[20px] items-center w-full text-pollingactive ">
                  {t('New to Crypto?')}
                  <Link
                    href="https://ethereum.org/wallets"
                    className="z-50 grid font-bold cursor-pointer text-fire1 hover:opacity-50"
                    rel="noreferrer"
                    target="_blank"
                  >
                    {t('Learn more about wallets')}
                  </Link>
                </Menu.Button>
              </>
            ) : (
              <>
                <Menu.Button
                  onClick={() => {
                    setSettings(true)
                    setDebouncedHover(false)
                    setHover(false)
                  }}
                  as="div"
                  className="pl-8 z-50 rounded-lg  cursor-pointer h-[40px] hover:opacity-80 hover:bg-gray-500  gap-2 px-2 text-primary flex items-center justify-between w-full min-w-full font-thin text-xxs tracking-default"
                >
                  {t('Settings')}
                  <Settings width={24} height={24} className="p-1 m-1 fill-current" />
                </Menu.Button>
                <Menu.Button
                  onClick={() => {
                    setDebouncedHover(false)
                    setHover(false)
                    disconnect()
                    updateConnection(false)
                  }}
                  as="div"
                  className="pl-8 z-50 rounded-lg  cursor-pointer h-[40px] hover:opacity-80 hover:bg-gray-500  gap-2 px-2 text-primary flex items-center justify-between w-full min-w-full font-thin text-xxs tracking-default"
                >
                  {t('Disconnect')}
                  <Logout />
                </Menu.Button>
              </>
            )}
          </Menu.Item>
          <div className="top-0 left-0 custom-backdrop-blur z-1" />
        </Menu.Items>
      </AnimationTransition>
    </span>
  )
}

/**
 * @return {React.ElementType<any>} Animated border on button
 */
function AnimatedButton({ isConnected, children }: { isConnected: boolean; children: ReactNode }) {
  return (
    <div
      className={classNames(
        isConnected
          ? 'hover:animate-border group-one-hover:animate-border group-hover:animate-border'
          : 'animate-border hover:animate-none group-one-hover:animate-none group-hover:animate-none',
        'duration-500 ease-in-out flex flex-col gap-2 space-y-2 rounded-lg  bg-gradient-to-r from-gradientborderone via-gradientbordertwo to-gradientborderthree bg-[length:200%_200%] p-[1px] transition  focus:outline-none'
      )}
    >
      <Menu
        as="div"
        className="relative whitespace-nowrap bg-secondarybg rounded-lg min-w-[260px] h-[35px] flex items-center justify-center w-full"
      >
        {children}
      </Menu>
    </div>
  )
}

/**
 * @return {React.ElementType<any>} MobileWrapper, wraps account in fixed position bottom with background if mobile
 */
function MobileWrapper({ children }: { children: ReactNode }) {
  return (
    <div className="fixed bottom-0 left-0 z-10 grid w-screen h-auto p-2 bg-black bg-opacity-50 justify-items-end pr-7 lg:bg-transparent landscape:md:bg-transparent landscape:transparent lg:p-0 landscape:p-0 lg:relative landscape:relative lg:w-auto landscape:w-auto lg:bottom-auto landscape:bottom-auto lg:left-auto landscape:left-auto">
      {children}
    </div>
  )
}
